<script>
import { Library } from "@observablehq/stdlib/";
import { D3Utility } from "../services/d3_utility.js";

export default {
  name: "ComboChart",
  props: {
    data: {
      required: true,
      type: Array,
    },
    chartData: {
      required: true,
      type: Object,
    },
    duration: {
      required: true,
      type: [String, Number],
    },
  },
  data() {
    return {
      xScale: null,
      line: null,
      lines: null,
      bars: null,
      yBarScale: null,
      yLineScale: null,
      gx: null,
      svg: null,
      xaxis: null,
      min_translate_x: null,
      max_translate_x: null,
      width: 700,
      height: 300,
      margin: 30,
      padding: 30,
      zoom_event: null,
      updated_data: null,
      clip: null,
      color: null,
      div: null,
      date_extent: null,
      legends: null,
    };
  },
  watch: {
    data: function (newVal, oldVal) {
      if (newVal && newVal.length > 0 && !this.svg) {
        this.init();
      } else if (this.svg && this.zoom_event) {
        this.updateChart();
        this.handleZoom(this.zoom_event);
      }
    },
    duration: function (newVal) {},
  },
  mounted() {
    if (this.data && this.data.length > 0) this.init();
  },
  methods: {
    init() {
      let self = this;
      let colorScheme = this.chartData.colors;
      const id = this.chartData.id;
      const width = D3Utility.get_conatiner_width(".container");
      this.width = width;
      const height = 300;
      const legendHeight = 100;
      const margin = 30;
      const padding = 30;
      this.chart_config = {
        width,
        height,
        legendHeight,
        margin,
        padding,
      };
      const zoom = D3Utility.define_restricted_zoom(this.chart_config, zoomed);

      this.updated_data = {
        line: [],
        bar: [],
      };
      this.data.map((elem) => {
        this.updated_data[elem.type].push(elem);
      });

      var keys =
        this.updated_data["bar"].map((elem) => {
          return elem.id;
        }) || [];

      this.updated_data["line"].map((elem) => {
        keys.push(elem.id);
      });

      this.color = D3Utility.get_scale_ordinal(keys, colorScheme);
      this.xScale = D3Utility.get_scaleband([margin, width - margin - padding]);

      this.color = D3Utility.get_scale_ordinal(keys, colorScheme);
      this.xScale = D3Utility.get_scaleband([margin, width - margin - padding]);
      this.yBarScale = D3Utility.get_linear_scale([height - margin - padding, margin]);
      this.yLineScale = D3Utility.get_linear_scale([height - margin - padding, margin]);
      this.xScale.domain(
        this.data[0].values.map(function (d) {
          return d.key;
        })
      );
      this.yLineScale.domain([0, D3Utility.get_max(this.updated_data["line"])]).nice();
      this.yBarScale.domain([0, D3Utility.get_max(this.updated_data["bar"]) || 10]);

      //-----------------------------AXES-----------------------------//

      var yBarAxis = (g, y) =>
        g.attr("transform", `translate(${width - 2 * margin},0)`).call(D3Utility.define_right_axis(y));
      var yLineAxis = (g, y) =>
        g.attr("transform", `translate(${margin + padding},0)`).call(D3Utility.define_left_axis(y));

      this.xaxis = (g, x) =>
        g
          .attr("transform", `translate(${margin},${height - margin - padding})`)
          .call(D3Utility.define_bottom_axis_non_con(x, this.xScale));
      this.div = D3Utility.append_tooltip();
      this.svg = D3Utility.create_svg(this.chartData.id, this.chart_config);
      const library = new Library();
      this.clip = library.DOM.uid("clip");

      this.svg = D3Utility.append_clip_path(this.chart_config, this.clip, this.svg);

      //----------------------------LINES-----------------------------//
      this.line = D3Utility.get_line_obj(this.xScale, this.yLineScale);

      //-------------------------2. DRAWING---------------------------//
      //-----------------------------AXES-----------------------------//
      this.gx = D3Utility.append_axis(this.svg, this.xaxis, this.xScale, padding, height - 2 * padding);
      this.gx
        .append("text")
        .attr("x", "650px")
        .attr("y", "30px")
        .attr("text-anchor", "middle")
        .text(this.chartData.xAxis);

      let yaxis = D3Utility.append_axis(this.svg, yLineAxis, this.yLineScale, 2 * padding, 0);
      yaxis
        .append("text")
        .attr("x", "-130px")
        .attr("y", "-40px")
        .attr("text-anchor", "middle")
        .attr("transform", "rotate(-90)")
        .text(this.chartData.yAxis);

      //----------------------------LINES-----------------------------//
      this.addLinesAndBars();

      if (this.updated_data["bar"].length > 0) {
        yaxis = D3Utility.append_axis(this.svg, yBarAxis, this.yBarScale, 0, 0);
        yaxis
          .append("text")
          .attr("x", "-130px")
          .attr("y", "40px")
          .attr("text-anchor", "middle")
          .attr("transform", "rotate(-90)")
          .text(this.chartData.yBarAxis);
      }

      this.svg
        .call(zoom)
        .transition()
        .duration(750)
        .call(zoom.scaleTo, this.duration == 1 ? 6 : 1);

      function zoomed(event) {
        self.zoom_event = event;
        self.handleZoom(event);
      }
    },
    handleZoom(event) {
      let self = this;
      this.xScale.range([this.margin, this.width - this.margin - this.padding].map((d) => event.transform.applyX(d)));
      this.lines.selectAll("path").attr("d", function (d) {
        return self.line(d.values);
      });
      this.lines
        .selectAll("circle")
        .attr("r", (d) => (event.transform.k * 0.5 > 2 ? 2 : event.transform.k * 0.5))
        .attr("cx", (d) => self.xScale(d.key));
      this.updated_data["bar"].forEach((ele, i) => {
        this.svg
          .selectAll(`#bars-${ele.id} rect`)
          .attr("x", function (d) {
            return self.xScale(d.key) - (self.xScale.bandwidth() / 4) * 2 * (i + 1);
          })
          .attr("width", this.xScale.bandwidth() / 2);
      });
      this.xaxis = (g, x) =>
        g
          .attr("transform", `translate(${this.margin},${this.height - this.margin - this.padding})`)
          .call(D3Utility.define_bottom_axis_non_con(x, self.xScale, event));

      this.gx.call(this.xaxis, this.xScale);
    },
    updateChart() {
      this.updated_data = {
        line: [],
        bar: [],
      };
      this.data.map((elem) => {
        this.updated_data[elem.type].push(elem);
      });
      let self = this;
      this.lines.remove();
      this.bars.remove();
      this.addLinesAndBars();
      this.gx.call(this.xaxis, this.xScale);
    },
    addLinesAndBars() {
      let self = this;

      this.bars = D3Utility.append_bars(
        this.svg,
        this.updated_data["bar"],
        this.chart_config,
        this.clip,
        this.xScale,
        this.color,
        this.xScale,
        this.yBarScale,
        this.div,
        this.chartData
      );

      this.lines = D3Utility.append_lines(
        this.svg,
        this.updated_data["line"],
        this.chart_config,
        this.clip,
        this.line,
        this.color,
        this.xScale,
        this.yLineScale,
        this.div,
        this.chartData
      );

      var keys =
        this.updated_data["bar"].map((elem) => {
          return { label: elem.label, id: elem.id };
        }) || [];

      this.updated_data["line"].map((elem) => {
        keys.push({ label: elem.label, id: elem.id });
      });

      // Add one dot in the legend for each name.
      this.legends = D3Utility.append_group(this.svg, 0, this.height);
      this.legends = D3Utility.append_legends(
        this.svg,
        this.chartData.id,
        0,
        this.height,
        keys,
        this.chart_config,
        this.color
      );
    },
  },
  computed: {},
};
</script>

<template>
  <div class="container">
    <div :id="`${chartData.id}`" class="svg-container"></div>
  </div>
</template>

<style lang="scss">
/* AXES */
/* ticks */
.axis line {
  stroke: #706f6f;
  stroke-width: 0.5;
  shape-rendering: crispEdges;
}

/* axis contour */
.axis path {
  stroke: #706f6f;
  stroke-width: 0.7;
  shape-rendering: crispEdges;
}

/* axis text */
.axis text {
  fill: #2b2929;
  font-family: "Roboto", sans-serif;
  font-size: 120%;
}
.serie_label {
  fill: #2b2929;
  font-family: "Roboto", sans-serif;
  font-size: 80%;
}
.zoom {
  cursor: move;
  fill: none;
  pointer-events: all;
}
.bars rect {
  opacity: 0.4;
  stroke: black;
}
.pointer {
  cursor: pointer;
}
div.tooltip {
  position: absolute;
  width: 200px;
  height: 110px;
  background-color: white;
  padding: 10px;
  font-size: 12px;
  pointer-events: none;
  border: none;
  box-shadow:
    -3px -1px 20px 0 rgba(0, 0, 0, 0.18),
    0 6px 20px 0 rgba(153, 155, 168, 0.1);
}
div.tooltip > div {
  margin: 2px 0;
}
.caption {
  color: var(--v-primaryGrey-base);
}
.shadow,
path:hover {
  -webkit-filter: drop-shadow(3px 3px 2px rgba(0, 0, 0, 0.7));
  filter: drop-shadow(3px 3px 2px rgba(0, 0, 0, 0.7));
}
rect:hover {
  -webkit-filter: drop-shadow(2px -2px 2px rgba(0, 0, 0, 0.7));
  filter: drop-shadow(2px -2px 2px rgba(0, 0, 0, 0.7));
}
</style>
